import { cn } from '@/lib/utils'
import { PeriodoPontoFactory } from '@acme/common'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStore } from 'store'
import SP, { type TipoPeriodo } from './SelectPeriodo'
import { HELPER_TEXT_FORM, SelectProps } from './types'

const FORMATO_COMPETENCIA = 'YYYY-MM' as const

interface IRootProps {
    label?: string
    value: string
    onChange: React.Dispatch<React.SetStateAction<string>>
    readOnly?: boolean
    width?: number | string
    hideButtons?: boolean
    /**
     * `form` = preset para formulários: largura 100%, size `medium` e
     * helperText reservado para manter o alinhamento com os demais campos.
     */
    variant?: SelectProps['variant'] | 'form'
    /** Classe do elemento raiz do select. */
    className?: string
    /** Texto de apoio abaixo do campo. */
    helperText?: string
}

/**
 * Componente para seleção de competência (YYYY-MM).
 * Internamente utiliza o SelectPeriodo configurado para modo 'competencia'.
 *
 * @example
 * const [competencia, setCompetencia] = useCompetencia()
 *
 * <SelectCompetencia
 *     value={competencia}
 *     onChange={setCompetencia}
 *     hideButtons
 * />
 */
export default function SelectCompetencia({
    label = 'Competência',
    onChange,
    value,
    readOnly,
    width,
    hideButtons,
    variant = 'filled',
    className,
    helperText,
}: IRootProps) {
    const isForm = variant === 'form'
    const selectHelperText =
        helperText ?? (isForm ? HELPER_TEXT_FORM : undefined)
    const empresa = useStore(state => state.company)

    // Converte competência (YYYY-MM) para período [dataInicial, dataFinal]
    const periodoValue = useMemo((): [string, string] => {
        if (!empresa) {
            // Fallback sem empresa: usa o primeiro e último dia do mês
            const dataInicial = dayjs(value, FORMATO_COMPETENCIA)
                .startOf('month')
                .format('YYYY-MM-DD')
            const dataFinal = dayjs(value, FORMATO_COMPETENCIA)
                .endOf('month')
                .format('YYYY-MM-DD')
            return [dataInicial, dataFinal]
        }

        const [dataInicial, dataFinal] = PeriodoPontoFactory.fromCompetencia(
            value,
            {
                diaInicioPonto: empresa.diaInicioPonto,
                trocasInicioPonto: empresa.TrocaInicioPonto,
            },
        ).getPeriodo()

        return [
            dataInicial.format('YYYY-MM-DD'),
            dataFinal.format('YYYY-MM-DD'),
        ]
    }, [value, empresa])

    const handlePeriodoChange: React.Dispatch<
        React.SetStateAction<[string, string]>
    > = useCallback(
        action => {
            if (typeof action === 'function') {
                onChange(prev => {
                    let currentPeriodo: [string, string]
                    if (!empresa) {
                        const dataInicial = dayjs(prev, FORMATO_COMPETENCIA)
                            .startOf('month')
                            .format('YYYY-MM-DD')
                        const dataFinal = dayjs(prev, FORMATO_COMPETENCIA)
                            .endOf('month')
                            .format('YYYY-MM-DD')
                        currentPeriodo = [dataInicial, dataFinal]
                    } else {
                        const [dataInicial, dataFinal] =
                            PeriodoPontoFactory.fromCompetencia(prev, {
                                diaInicioPonto: empresa.diaInicioPonto,
                                trocasInicioPonto: empresa.TrocaInicioPonto,
                            }).getPeriodo()
                        currentPeriodo = [
                            dataInicial.format('YYYY-MM-DD'),
                            dataFinal.format('YYYY-MM-DD'),
                        ]
                    }

                    const [, newDataFinal] = action(currentPeriodo)

                    if (!empresa) {
                        return dayjs(newDataFinal).format(FORMATO_COMPETENCIA)
                    }

                    const [competencia] = PeriodoPontoFactory.fromPeriodo(
                        newDataFinal,
                        newDataFinal,
                    ).getCompetencias({
                        diaInicioPonto: empresa.diaInicioPonto,
                        trocasInicioPonto: empresa.TrocaInicioPonto,
                    })
                    return competencia
                })
            } else {
                const [, newDataFinal] = action
                if (!empresa) {
                    onChange(dayjs(newDataFinal).format(FORMATO_COMPETENCIA))
                    return
                }

                const [competencia] = PeriodoPontoFactory.fromPeriodo(
                    newDataFinal,
                    newDataFinal,
                ).getCompetencias({
                    diaInicioPonto: empresa.diaInicioPonto,
                    trocasInicioPonto: empresa.TrocaInicioPonto,
                })
                onChange(competencia)
            }
        },
        [onChange, empresa],
    )

    const computedWidth = (() => {
        if (width != null) return width
        if (isForm) return '100%'
        if (hideButtons) return 220
        return 320
    })()

    const [type] = useState<TipoPeriodo>('competencia')

    return (
        <SP.Root
            label={label}
            value={periodoValue}
            onChange={handlePeriodoChange}
            allowedTypes={['competencia']}
            width={computedWidth}
            type={type}
            className={cn(isForm && 'items-start', className)}
        >
            <SP.DateSelector
                readOnly={readOnly}
                helperText={selectHelperText}
                containerClassName={isForm ? 'flex-1' : undefined}
                variant={variant === 'outlined' ? 'outlined' : 'filled'}
                size={isForm ? 'medium' : 'small'}
            />
            {!hideButtons && (
                <SP.Buttons className={isForm ? 'h-14' : undefined} />
            )}
        </SP.Root>
    )
}

type DefaultCompetencia =
    | string
    | ((value: string, formato: typeof FORMATO_COMPETENCIA) => string)

export function useCompetencia(defaultValue?: DefaultCompetencia) {
    const empresa = useStore(state => state.company)
    const competenciaAberta = empresa?.competenciaAtual

    const DEFAULT = (() => {
        if (typeof defaultValue === 'function')
            return defaultValue(competenciaAberta || '', FORMATO_COMPETENCIA)
        if (typeof defaultValue === 'string') return defaultValue
        return dayjs(competenciaAberta).format(FORMATO_COMPETENCIA)
    })()

    const [competencia, _set] = useState<string>(DEFAULT)

    function setCompetencia(
        value: string | ((prev: string) => string) | null | dayjs.Dayjs,
    ) {
        if (value === null) return _set(DEFAULT)

        if (dayjs.isDayjs(value)) {
            _set(value.format(FORMATO_COMPETENCIA))
        } else if (typeof value === 'function') {
            _set(prev => {
                const newValue = value(prev)
                return newValue
            })
        } else {
            _set(value)
        }
    }

    useEffect(() => {
        if (
            empresa &&
            empresa.competenciaAtual &&
            (typeof defaultValue === 'function'
                ? competencia !=
                  defaultValue(empresa.competenciaAtual, FORMATO_COMPETENCIA)
                : competencia != empresa.competenciaAtual)
        ) {
            setCompetencia(
                typeof defaultValue === 'function'
                    ? defaultValue(
                          empresa.competenciaAtual,
                          FORMATO_COMPETENCIA,
                      )
                    : dayjs(empresa.competenciaAtual).format(
                          FORMATO_COMPETENCIA,
                      ),
            )
        }
    }, [empresa?.id])

    return [competencia, setCompetencia] as const
}
